import * as React from "react";
import { createRoot } from "react-dom/client";
import { FluentProvider } from "@fluentui/react-components";
import { AuthProvider } from "react-oidc-context";
import { getIdpConfig, loadConfig } from "../config/auth0";
import { AppProvider } from "./components/context/AppContext";
import { customLightTheme } from "./components/style/theme";

const rootElement: HTMLElement = document.getElementById("container");
const root = createRoot(rootElement);
const OFFICE_READY_TIMEOUT_MS = 4000;

const waitForOfficeReady = () => {
  if (typeof Office === "undefined" || typeof Office.onReady !== "function") {
    return Promise.resolve({ host: undefined, platform: undefined });
  }

  return Promise.race([
    Office.onReady(),
    new Promise((resolve) => {
      setTimeout(() => {
        console.warn("Office.onReady timed out; continuing app startup");
        resolve({ host: undefined, platform: undefined });
      }, OFFICE_READY_TIMEOUT_MS);
    }),
  ]);
};

const renderApp = async () => {
  const App = (await import("./components/App")).default;

  root.render(
    <FluentProvider theme={customLightTheme}>
      <AuthProvider {...getIdpConfig()}>
        <AppProvider>
          <App />
        </AppProvider>
      </AuthProvider>
    </FluentProvider>
  );
};

const initializeApp = async () => {
  try {
    await waitForOfficeReady();
    await loadConfig();
    await renderApp();
  } catch (error) {
    console.error("Failed to initialize the application:", error);
    try {
      await loadConfig();
      await renderApp();
    } catch (renderError) {
      console.error("Failed to render the application:", renderError);
    }
  }
};

initializeApp();

if ((module as any).hot) {
  (module as any).hot.accept("./components/App", async () => {
    const NextApp = (await import("./components/App")).default;
    root.render(<NextApp />);
  });
}
